import React, { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../common/Dialog";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import Button from "../common/Button";

// Diálogo de consumo: antes de guardar la historia clínica se confirma qué insumos
// se usaron realmente del tratamiento (ajustables con +/-). No modifica el tratamiento,
// solo las cantidades a descontar del stock.
export default function TreatmentConsumptionDialog({ treatment, open, onConfirm, onCancel, saving }) {
  const { t } = useBrandTranslation();
  // quantities: { [supply_id]: quantity_used }
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    if (!treatment) return;
    const q = {};
    (treatment.items || []).forEach((it) => {
      q[it.supply_id] = Number(it.quantity) || 0;
    });
    setQuantities(q);
  }, [treatment]);

  const step = (supplyId, delta) => {
    setQuantities((prev) => {
      const next = Math.max(0, Number(prev[supplyId] || 0) + delta);
      return { ...prev, [supplyId]: next };
    });
  };

  const handleConfirm = () => {
    const items = (treatment.items || []).map((it) => ({
      supply_id: it.supply_id,
      description: it.supply?.description || "",
      quantity_used: Number(quantities[it.supply_id] || 0),
    }));
    onConfirm(items);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o && !saving ? onCancel() : null)}>
      <DialogContent className="w-[94vw] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogTitle className="text-lg font-semibold text-gray-900 mb-1">
          {t("inventory.consumption.title", "Consumo de insumos")}
        </DialogTitle>
        <p className="text-sm text-gray-600 mb-4">
          {t(
            "inventory.consumption.question",
            '¿Utilizaste todos los insumos de "{{name}}"? Modificalas en la siguiente tabla en caso de ser necesario y se actualizará el stock.',
            { name: treatment?.name || "" }
          )}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">{t("inventory.consumption.supply", "Insumo")}</th>
                <th className="py-2 pr-3 text-center w-40">
                  {t("inventory.consumption.quantity", "Cantidad utilizada")}
                </th>
              </tr>
            </thead>
            <tbody>
              {(treatment?.items || []).map((it) => (
                <tr key={it.supply_id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium text-gray-800">
                    {it.supply?.description || "—"}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => step(it.supply_id, -1)}
                        className="p-1 rounded-full border border-gray-300 hover:bg-gray-100 text-gray-700"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-10 text-center font-semibold">
                        {quantities[it.supply_id] ?? 0}
                      </span>
                      <button
                        type="button"
                        onClick={() => step(it.supply_id, 1)}
                        className="p-1 rounded-full border border-gray-300 hover:bg-gray-100 text-gray-700"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            {t("inventory.consumption.cancel", "Cancelar")}
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving
              ? t("inventory.common.saving", "Guardando…")
              : t("inventory.consumption.confirm", "Confirmar")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
